import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface Novidade {
  id: string;
  titulo: string;
  descricao: string;
  link: string | null;
  icone: string | null;
  metodo_slug: string | null;
  card_path: string | null;
  ativo: boolean;
  publicado_em: string;
}

export const useNovidades = () => {
  const { user } = useAuth();
  const [novidades, setNovidades] = useState<Novidade[]>([]);
  const [naoLidas, setNaoLidas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    const { data: ativas } = await supabase
      .from("novidades")
      .select("*")
      .eq("ativo", true)
      .order("publicado_em", { ascending: false });
    const lista = (ativas || []) as Novidade[];
    setNovidades(lista);

    if (user) {
      const { data: lidas } = await supabase
        .from("novidades_lidas")
        .select("novidade_id")
        .eq("user_id", user.id);
      const idsLidas = new Set((lidas || []).map((r: any) => r.novidade_id as string));
      setNaoLidas(new Set(lista.filter((n) => !idsLidas.has(n.id)).map((n) => n.id)));
    } else {
      setNaoLidas(new Set());
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const marcarLida = useCallback(
    async (id: string) => {
      if (!user) return;
      await supabase
        .from("novidades_lidas")
        .insert({ user_id: user.id, novidade_id: id });
      setNaoLidas((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [user?.id]
  );

  // helper: tem novidade não lida apontando para esse path?
  const hasNovaForPath = useCallback(
    (path: string) =>
      novidades.some((n) => naoLidas.has(n.id) && n.card_path === path),
    [novidades, naoLidas]
  );

  return { novidades, naoLidas, loading, marcarLida, hasNovaForPath, recarregar: carregar };
};
